import React, { useState, useEffect } from "react";
import { UniversityCard } from "../../../components/index";
import { handleApi } from "../../../utils/authentication";
import { toast } from "react-toastify";
import no_image from "../../../assests/no_image.jpg";
import { useSelector, useDispatch } from "react-redux";
import { UPDATE_USER_DATA } from "../../../redux/features/user/userSlice";
import { Navigate } from "react-router-dom";
import LoadingSpinner from "../../../components/client/LoadingSpinner";
import { driver } from 'driver.js';
import { homePageSteps } from "../client-tour/homepage";
import { useNavigate } from "react-router-dom";
import { UPDATE_USER_TOUR } from "../../../redux/features/user/userTourSlice";
import { updateApplicationTourStatus } from "../../../services/ApiService";
import {
  clientHasAccess,
  applySubscriptionAccess,
} from "../../../utils/subscriptionAccess";
import {
  CLIENT_DASHBOARD_PATH,
  getClientLandingPath,
} from "../../../utils/clientRouting";

const Home = () => {
  const [clientList, setClientList] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state?.user);
  const tour = useSelector((state) => state?.tour);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const shouldSkipClientPicker =
    getClientLandingPath(user) === CLIENT_DASHBOARD_PATH;

  const getClientList = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("user_id", user?.user_id);
      const result = await handleApi("user_client_mapping", "POST", {}, formData);

      if (result?.success) {
        const sortedClients = result.data.user_client_mapping_info.sort((a, b) =>
          a.client_name.localeCompare(b.client_name)
        );
        setClientList(sortedClients);

        if (
          sortedClients.length === 1 &&
          clientHasAccess(sortedClients[0]) &&
          !sortedClients[0].is_blocked
        ) {
          if (sortedClients[0].subscription_access) {
            applySubscriptionAccess(sortedClients[0].subscription_access);
          }
          dispatch(
            UPDATE_USER_DATA({
              client_id: sortedClients[0].client_id,
              client_name: sortedClients[0].client_name,
              total_members: sortedClients[0].total_members,
              is_agency: sortedClients[0].is_agency,
              role_id: sortedClients[0].role_id,
            }),
          );
          navigate("/client/dashboards/home");
        }
        
      } else {
        console.error(result?.error?.error_message);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Failed to fetch client list", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shouldSkipClientPicker) {
      return;
    }
    getClientList();
  }, [shouldSkipClientPicker]);

  useEffect(() => {
    if (shouldSkipClientPicker) {
      return undefined;
    }

    // This page is structurally different from the chained tours and keeps
    // driver.js' built-in buttons, so it does not share the `useClientTour`
    // hook. We still need the cleanup-on-unmount guarantee that the hook
    // gives the other pages, which is why the driver instance is held in a
    // local and destroyed when the component unmounts.
    if (!(!user?.has_seen_tour && !tour?.welcome_page && user?.is_client)) {
      return undefined;
    }

    const highlightTimers = [];

    const tourDriver = driver({
      popoverClass: 'driverjs-theme',
      // Modal behavior: outside clicks and Esc must not dismiss the tour.
      allowClose: false,
      onCloseClick: async () => {
        try {
          if (user) {
            const result = await updateApplicationTourStatus(user);
            if (result?.success) {
              dispatch(UPDATE_USER_DATA({ has_seen_tour: result?.data?.has_seen_tour }));
            }
          }
        } finally {
          dispatch(UPDATE_USER_TOUR({
            welcome_page: true,
            main_dashboard: true,
            settings_page: true,
            programs_page: true,
            campaigns_page: true,
            integration_page: true,
            members_page: true,
            add_member_page: true,
          }));
          tourDriver.destroy();
          navigate('/client/dashboards/home');
          toast.info("Tour skipped");
        }
      },
      steps: homePageSteps.map((step, index) => ({
        ...step,
        popover: {
          ...step.popover,
          title: `<div style="display:flex;justify-content:space-between;align-items:center;"> <p style="margin-bottom:0rem">${step?.popover.title}</p> <p style="margin-bottom:0rem;margin-right: 2rem;">${index + 1} of ${homePageSteps.length}</p></div>`,
        },
      })),
      onHighlightStarted: () => {
        highlightTimers.push(
          setTimeout(() => {
            dispatch(UPDATE_USER_TOUR({ welcome_page: true }));
          }, 15000),
        );
      },
      onDestroyed: () => {
        dispatch(UPDATE_USER_TOUR({ welcome_page: true }));
      },
    });

    tourDriver.drive();

    return () => {
      tourDriver.destroy();
      highlightTimers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSkipClientPicker]);

  if (shouldSkipClientPicker) {
    return <Navigate to={CLIENT_DASHBOARD_PATH} replace />;
  }

  return (
    <div >
      {loading && <LoadingSpinner />}
      <div className="row row-cols-1 row-cols-lg-2 g-4 select-client">
        {clientList.map((client) => (
          <div className="col " key={client.client_id}>
            <UniversityCard
              client_id={client.client_id}
              university_name={client.client_name}
              total_members={client.total_members}
              university_logo={client.client_logo_path || no_image}
              subscription_start_date={client.subscription_start_date}
              subscription_end_date={client.subscription_end_date}
              is_blocked={client.is_blocked}
            
              is_subscription_expired={client.is_subscription_expired}
              client_data={client}
              total_spend_value="$00,000"
              total_spend_percentage="+00.0%"
              impressions_value="00,000"
              impressions_percentage="+00.0%"
              leads_value="000"
              leads_percentage="+00.0%"
            />
          </div>
        ))}
      </div>
      {!loading && clientList.length === 0 && (
        <div className="position-absolute" style={{ top: "50%", left: "40%" }}>
          <h5>No Client Details Found</h5>
        </div>
      )}
    </div>
  );
};

export default Home;
